/**
 * CandidateFormPage — public, no-auth "information form" page for the
 * Company Dashboard hiring pipeline's Form Collection stage. Reached via the
 * link emailed by send_form_invite_email() (email_service.py / Resend).
 *
 * Native to Vagmee, not Google Forms — same public-link model already used
 * for the AI Interview/MCQ/Coding candidate routes (bare, unauthenticated,
 * keyed by an unguessable Firestore document id). Fields are fully
 * recruiter-configurable (FormFieldsEditorModal.jsx / form_validation.py on
 * the backend) — this renders whatever type/validation/required config the
 * recruiter set, and mirrors form_validation.py's rules client-side for
 * instant feedback. The server re-validates everything on submit regardless
 * (see pipeline_service.submit_candidate_form) — this is UX, not the gate.
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getCandidateFormConfig, submitCandidateForm } from "../services/jobService";
import "../InstitutionalDashboard.css";
import "./RecruiterJobs.css";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+\-()\s]{7,20}$/;
const URL_RE = /^https?:\/\/[^\s]+\.[^\s]+/;

function validateField(field, value) {
  const required = !!field.required;
  const validation = field.validation || {};
  const isEmpty = value == null || value === "" || (Array.isArray(value) && value.length === 0);

  if (isEmpty) return required ? "This field is required." : null;

  switch (field.type) {
    case "text":
    case "textarea": {
      const text = String(value);
      if (validation.minLength != null && text.length < validation.minLength) return `Enter at least ${validation.minLength} characters.`;
      if (validation.maxLength != null && text.length > validation.maxLength) return `Enter at most ${validation.maxLength} characters.`;
      if (validation.pattern) {
        try { if (!new RegExp(`^(?:${validation.pattern})$`).test(text)) return "Doesn't match the expected format."; } catch { /* ignore bad pattern */ }
      }
      return null;
    }
    case "number": {
      const num = Number(value);
      if (Number.isNaN(num)) return "Enter a valid number.";
      if (validation.min != null && num < validation.min) return `Must be at least ${validation.min}.`;
      if (validation.max != null && num > validation.max) return `Must be at most ${validation.max}.`;
      return null;
    }
    case "email":
      return EMAIL_RE.test(String(value).trim()) ? null : "Enter a valid email address.";
    case "phone":
      return PHONE_RE.test(String(value).trim()) ? null : "Enter a valid phone number.";
    case "url":
      return URL_RE.test(String(value).trim()) ? null : "Enter a valid URL (starting with http:// or https://).";
    case "date": {
      if (validation.minDate && value < validation.minDate) return `Date must be on or after ${validation.minDate}.`;
      if (validation.maxDate && value > validation.maxDate) return `Date must be on or before ${validation.maxDate}.`;
      return null;
    }
    case "select":
    case "radio":
      return (field.options || []).includes(value) ? null : "Choose one of the listed options.";
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      if (validation.minSelected != null && arr.length < validation.minSelected) return `Choose at least ${validation.minSelected}.`;
      if (validation.maxSelected != null && arr.length > validation.maxSelected) return `Choose at most ${validation.maxSelected}.`;
      return null;
    }
    case "yesno":
      return value === "yes" || value === "no" ? null : "Choose Yes or No.";
    default:
      return null;
  }
}

function FieldInput({ field, value, onChange }) {
  switch (field.type) {
    case "textarea":
      return <textarea rows={4} placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "number":
      return <input type="number" placeholder={field.placeholder || ""} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "email":
      return <input type="email" placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "phone":
      return <input type="tel" placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "url":
      return <input type="url" placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "date":
      return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "select":
      return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    case "radio":
      return (
        <div className="instc-form-choice-group">
          {(field.options || []).map((opt) => (
            <label key={opt} className="instc-form-choice">
              <input type="radio" name={field.key} checked={value === opt} onChange={() => onChange(opt)} />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    case "checkbox": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (opt) => onChange(arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt]);
      return (
        <div className="instc-form-choice-group">
          {(field.options || []).map((opt) => (
            <label key={opt} className="instc-form-choice">
              <input type="checkbox" checked={arr.includes(opt)} onChange={() => toggle(opt)} />
              <span>{opt}</span>
            </label>
          ))}
        </div>
      );
    }
    case "yesno":
      return (
        <div className="instc-form-choice-group instc-form-choice-group--row">
          {["yes", "no"].map((opt) => (
            <label key={opt} className="instc-form-choice">
              <input type="radio" name={field.key} checked={value === opt} onChange={() => onChange(opt)} />
              <span>{opt === "yes" ? "Yes" : "No"}</span>
            </label>
          ))}
        </div>
      );
    case "text":
    default:
      return <input type="text" placeholder={field.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

export default function CandidateFormPage({ AppBackground }) {
  const { candidateId } = useParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getCandidateFormConfig(candidateId);
        if (cancelled) return;
        setConfig(data);
        setSubmitted(!!data.alreadySubmitted);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load this form.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [candidateId]);

  const handleChange = (key, value) => {
    setValues((v) => ({ ...v, [key]: value }));
    setFieldErrors((e) => (e[key] ? { ...e, [key]: null } : e));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const errors = {};
    for (const field of config?.fields || []) {
      const msg = validateField(field, values[field.key]);
      if (msg) errors[field.key] = msg;
    }
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      await submitCandidateForm(candidateId, values, consent);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Unable to submit the form. Please try again.");
      if (err.fieldErrors) setFieldErrors(err.fieldErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      {AppBackground && <AppBackground />}
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "2rem" }}>
        {loading ? (
          <div className="inst-mcq-loading">Loading form…</div>
        ) : error && !config ? (
          <div className="inst-mcq-error-card">
            <div className="inst-mcq-error-icon">⚠️</div>
            <h2>Form Unavailable</h2>
            <p>{error}</p>
          </div>
        ) : submitted ? (
          <div className="instc-done">
            <div className="instc-done-icon">✓</div>
            <h2>Thanks, {config?.candidateName || "there"}!</h2>
            <p>Your details have been submitted for {config?.jobTitle || "this role"} at {config?.companyName || "the hiring team"}.</p>
            <p className="instc-done-sub">You may close this tab.</p>
          </div>
        ) : (
          <form className="job-form recruiter-modal" style={{ width: "min(560px, 100%)" }} onSubmit={handleSubmit}>
            <div className="recruiter-modal-header">
              <div>
                <h2>Additional details for {config?.jobTitle || "your application"}</h2>
                <p>{config?.companyName ? `${config.companyName} — ` : ""}Hi {config?.candidateName || "there"}, please fill in a few details.</p>
              </div>
            </div>
            {error && <div className="job-form-error">{error}</div>}
            {(config?.fields || []).map((field) => (
              <label key={field.key} className="instc-form-field">
                <span>{field.label}{field.required && <span className="instc-form-required"> *</span>}</span>
                {field.helpText && <small className="instc-form-help">{field.helpText}</small>}
                <FieldInput field={field} value={values[field.key]} onChange={(v) => handleChange(field.key, v)} />
                {fieldErrors[field.key] && <span className="instc-form-field-error">{fieldErrors[field.key]}</span>}
              </label>
            ))}

            <div className="instc-privacy-notice">
              <div className="instc-privacy-notice-title">Privacy Notice</div>
              <p>
                {config?.companyName || "The hiring team"} is collecting the details on this
                form — {(config?.fields || []).map((f) => f.label).join(", ") || "the fields above"} —
                to evaluate your application for {config?.jobTitle || "this role"} and, if you
                progress, to contact you about next steps. This is in addition to the resume and
                contact details already shared with your application.
              </p>
              <p>
                You may request access to, correction of, or deletion of your data, or withdraw
                your consent at any time, by contacting {config?.companyName || "the hiring team"}.
                Withdrawing consent may affect our ability to continue considering your
                application. Your data is retained only as long as needed for this recruitment
                process, unless a longer period is required by law.
              </p>
              <label className="instc-privacy-consent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  I have read this notice and consent to my personal data being processed for
                  this recruitment process, in accordance with the Digital Personal Data
                  Protection Act, 2023.
                </span>
              </label>
            </div>
            <div className="recruiter-modal-actions">
              <button className="primary-button" type="submit" disabled={submitting || !consent}>
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
